import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/next';
import type { NextPage } from 'next';
import type { AppProps, AppType } from 'next/app';
import Head from 'next/head';
import type { ReactElement, ReactNode } from 'react';
import { useEffect } from 'react';
import { DefaultLayout } from '@/code/frontend/components/DefaultLayout';
import '@/code/frontend/components/employee/weeklyStats/weekly-stats-theme.css';
import '@/code/frontend/styles/globals.css';
import { getCookie, setCookie } from '@/code/lib/utils/cookies';
import { FAVICON_HREF, tabTitle } from '@/code/lib/utils/deploymentEnv';
import { trpc } from '@/code/lib/utils/trpc';

export type NextPageWithLayout<
  TProps = Record<string, unknown>,
  TInitialProps = TProps,
> = NextPage<TProps, TInitialProps> & {
  getLayout?: (page: ReactElement) => ReactNode;
};

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};

const MyApp = (({ Component, pageProps }: AppPropsWithLayout) => {
  const getLayout =
    Component.getLayout ?? ((page) => <DefaultLayout>{page}</DefaultLayout>);

  // Handle referral code from URL
  useEffect(() => {
    // Check for referral code in URL or cookie
    const urlParams = new URLSearchParams(window.location.search);
    const urlReferralCode = urlParams.get('refer');
    const cookieReferralCode = getCookie('auriumReferralCode');

    const referralCode = urlReferralCode || cookieReferralCode;

    if (referralCode) {
      // If we have a new referral code from URL, or no cookie is set, update the cookie
      if (urlReferralCode || !cookieReferralCode) {
        setCookie('auriumReferralCode', referralCode, 30); // 30 days expiration
      }

      // Track APP_VISITED event (only if we have a referral code)
      // This will be deduplicated on the server side
      fetch('/api/referrals/track-event', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          referralCode,
          eventType: 'APP_VISITED',
          metadata: {
            source: urlReferralCode ? 'url' : 'cookie',
            page: window.location.pathname,
          },
        }),
      }).catch(() => {
        // Silently fail - don't break the app if tracking fails
      });
    }
  }, []); // Run only once on mount

  return (
    <>
      <Head>
        <title>{tabTitle('Aurium')}</title>
        <link rel="icon" href={FAVICON_HREF} key="favicon" />
      </Head>
      {getLayout(<Component {...pageProps} />)}
      <Analytics />
      <SpeedInsights />
    </>
  );
}) as AppType;

export default trpc.withTRPC(MyApp);
